import * as React from "react";
import { ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthUseCase } from "@gui/contexts/useCases/UseCasesContext";
import { Oops } from "../../../utilities/oops";
import { DEFAULT_PAGE } from "../../../utilities/constants";
import { NEW_USER_TOKEN } from "@infra/implementations/api/common";
import { RegisterProps, RegisterViewModel } from "./register.types";
import { logger } from "../../../utilities/logger";
import { LogData } from "@rogoag/shared-ts";
import { EMAIL_PATTERN, PASSWORD_MIN_LENGTH } from "../common";

export function useRegisterViewModel(props: RegisterProps): RegisterViewModel {
	const auth = useAuthUseCase();
	const [username, setEmail] = React.useState("");
	const [firstName, setFirstName] = React.useState("");
	const [lastName, setLastName] = React.useState("");
	const [emailError, setEmailError] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [confirmPasswordError, setConfirmPasswordError] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState("");

	const navigate = useNavigate();

	function isUsernameAndPasswordValid() {
		let isValid = true;

		if (!username) {
			setEmailError("Email cannot be empty");
			setLoading(false);
			isValid = false;
		}
		if (!password) {
			setPasswordError("Password cannot be empty");
			setLoading(false);
			isValid = false;
		}

		if (!confirmPassword) {
			setConfirmPasswordError("Password cannot be empty");
			setLoading(false);
			isValid = false;
		}

		if (password !== confirmPassword) {
			setConfirmPasswordError("Passwords do not match");
			setLoading(false);
			isValid = false;
		}

		return isValid;
	}

	const handleSubmit = async (event: { preventDefault: () => void }) => {
		const logData: LogData = {
			function: "register.viewmodel.handleSubmit",
			username: username,
		};
		event.preventDefault();
		setLoading(true);
		if (emailError || passwordError || confirmPasswordError) {
			setLoading(false);
			return;
		}
		setError("");

		if (!isUsernameAndPasswordValid()) {
			return;
		}

		try {
			logger.log("Registering user", logData);
			const result = await auth.register({ username, password, firstName, lastName });
			if (result.ok) {
				logger.log("User registered successfully", logData);
				const user = result.value;
				if (user.token == NEW_USER_TOKEN) {
					navigate("/registration-notice");
				} else {
					props.setLoggedIn(user as any);
					navigate(DEFAULT_PAGE);
				}
				return;
			}
			logger.warning(`Register failed: ${result.error}`, logData);
			switch (result.error) {
				case "email_already_exists":
					setError("An account with this email already exists. Please log in instead.");
					break;
				case "invalid_input":
					setError("Could not register — please check your inputs and try again.");
					break;
			}
		} catch (error: any) {
			Oops.handleError(error, "Authentication.Register.handleSubmit", username);
		} finally {
			setLoading(false);
		}
	};

	function handleEmailChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		setEmailError("");
		// validate that username is an email with regex
		if (!EMAIL_PATTERN.test(event.target.value.toString())) {
			setEmailError("Invalid email address");
		}

		setEmail(event.target.value);
	}

	function handlePasswordChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		setPasswordError("");
		const password = event.target.value;
		if (password == "") {
			setPasswordError("Password cannot be empty");
			return;
		}

		if (password.length < PASSWORD_MIN_LENGTH) {
			setPasswordError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
			return;
		}

		setPassword(password);
		if (password != confirmPassword) {
			setConfirmPasswordError("Passwords do not match");
			return;
		}
	}

	function handleConfirmPasswordChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		setConfirmPasswordError("");
		const confirmPassword = event.target.value;
		if (confirmPassword == "") {
			setConfirmPasswordError("Password cannot be empty");
			return;
		}

		if (confirmPassword.length < PASSWORD_MIN_LENGTH) {
			setConfirmPasswordError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
			return;
		}

		setConfirmPassword(password);
		if (confirmPassword != password) {
			setConfirmPasswordError("Passwords do not match");
			return;
		}
	}

	function handleFirstNameChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		setFirstName(event.target.value);
	}

	function handleLastNameChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		setLastName(event.target.value);
	}

	return {
		username,
		firstName,
		lastName,
		password,
		confirmPassword,
		loading,
		emailError,
		passwordError,
		confirmPasswordError,
		error,
		handleEmailChange,
		handlePasswordChange,
		handleConfirmPasswordChange,
		handleFirstNameChange,
		handleLastNameChange,
		handleSubmit,
	};
}
